import { FeatureFlagIds } from "../../utils/featureFlagIds";
import { isFinicityEnabled } from "../../utils/finicityUtils";
import React, {JSX, useEffect, useState} from "react";
import styles from "./_application-summary-card.module.scss";
import {
  usesFinanceQuote,
  FilledTemplateSubjectAndBody, FinanceQuote,
  getFullName, getNumberOfPaymentsPerYearForCarletonPaymentFrequency,
  hasValue,
  LinkType,
  MessageTemplates,
  PermissionSet,
  SimpleEmailMessage,
  SimpleMessagingDialog,
  SimpleTextMessage,
  toDollars
} from "@amsanalytics/react-shared-library";
import { TabName } from "../Views/OrderEditor/OrderEditorTabs";
import {
  getAge,
  getApplicant,
  getCurrentResidenceTime,
  getPrimaryJobTime,
  validateFinicityInfo
} from "../../utils/appUtils";
import { IApplication } from "../../Interfaces/Application/IApplication";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import { ICustomer } from "../../Interfaces/Customer/ICustomer";
import {
  capitalizeFirstLetterOfEachWord,
  formatDateOrTimeString,
  getCustomerPortalLinkMenuOptionsByRole, 
  getPaymentToIncomeRatioWithPayment,
  getTotalRepossessions,
  userHasPermission
} from "../../utils/utils";
import { Accordion, AccordionDetails, AccordionSummary, Button, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IConfiguration, useConfiguration } from "../../Data/ConfigurationData";
import {
  debtToIncome,
  getConvertedMonthlyPaymentAmount,
  getCustomFieldValue,
  getIncomeAndDebtBreakdownInternal,
  paymentToIncome
} from "../../utils/quoteUtils";
import { useDispatch, useSelector } from "react-redux";
import { IAppCustomer } from "../../Interfaces/Application/IAppCustomer";
import { isNotNil } from "ramda";
import { useDialog } from "../../providers/DialogProvider";
import ConsentHistoryDialog from "../Dialogs/ConsentHistoryDialog/ConsentHistoryDialog";
import { ConsentHistoryItem } from "../../Interfaces/Customer/ConsentHistoryItem";
import { AppState } from "../../store/rootReducer";
import Big from "big.js";
import { IEntityCustomField } from "../../Interfaces/Application/IEntityCustomField";
import { KeyFactorThresholdSection, KeyFactorValidationResult } from "../../Data/KeyFactorThreshold";
import { IApplicationLeaseQuote } from "../../Interfaces/Application/IApplicationLeaseQuote";
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { getCustomerCreditValue } from "../../Interfaces/Customer/ICustomerCredit";
import {
  generateCustomerPortalFinicityLinkUtil,
  getFilledTemplate,
  sendFinicityEmailMessagesToContactUtil,
  sendFinicityTextMessagesToContactUtil
} from "../../utils/portalLinkUtils";
import ErrorDialog from "../Dialogs/ErrorDialog";
import { ApplicationDispatch } from "../../store/application/types";
import { IEmployee } from "../../Interfaces/Admin/IEmployee";
import { ILoggedInEmployee } from "../../Interfaces/Admin/ILoggedInEmployee";
import { businessRuleOverrideStatusTypes, Constants, permissions } from "../../utils/constants";
import { MS_PER_MINUTE } from "@progress/kendo-date-math";
import { getCurrentFincityStatus } from "../../utils/finicityUtils";
import { BusinessRuleOverride } from "../../utils/BusinessRulesEngine";
import { IndirectQuoteType } from "../../Data/IndirectQuoteType";
import { IQuote } from "../../Interfaces/Common/IQuote";

// Function that returns any key factor validation results that match the given list of fieldNames. This is because
// a key factor have multiple fields that need to be validated, such as max/min values. We need to see if any of them
// failing. If so, we return the first one that fails. Otherwise, return the one that passes.
const getKeyFactorValidationResult = (keyFactorValidationResults: KeyFactorValidationResult[], fieldNames: string[]): KeyFactorValidationResult | undefined => {
  // Get failing items
  const failingItems = keyFactorValidationResults.filter(kfvr => fieldNames.map(fn => fn.toLowerCase()).includes(kfvr.threshold.fieldName.toLowerCase()) && kfvr.section === KeyFactorThresholdSection.Bad);
  if (failingItems.length > 0) {
    return failingItems[0];
  }
  // return the first passing item
  return keyFactorValidationResults.find(kfvr => fieldNames.map(fn => fn.toLowerCase()).includes(kfvr.threshold.fieldName.toLowerCase()) && kfvr.section !== KeyFactorThresholdSection.Bad);
}

interface ApplicationSummaryCardProps {
  currentApp: IApplication;
  contactDetailUrl?: string; // url to contact detail page in CRM
  navigate: (tabName: TabName) => void;
}

export const ApplicationSummaryCard: React.FC<ApplicationSummaryCardProps> = (props) => {
  const { data: configuration } = useConfiguration();
  const primary = getApplicant(props.currentApp, Constants.roleApplicantId, true);
  const coApp1 = getApplicant(props.currentApp, Constants.roleCoApplicant1Id, true);
  const coApp2 = getApplicant(props.currentApp, Constants.roleCoApplicant2Id, true);
  const [tabSelected, setTabSelected] = useState(0);
  const [openDialog, closeDialog] = useDialog();
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const [consentHistoryCustomer, setConsentHistoryCustomer] = useState<{ name: string, items: ConsentHistoryItem[] } | null>(null);
  const dispatch = useDispatch<ApplicationDispatch>();
  const activeUser: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const employees: IEmployee[] = useSelector((state: AppState) => state.applications.employees);
  const currentEmployee = employees.find((e) => e.id === activeUser.id);
  // Finicity is licensed per client (FinicityEnabled flag); without it the buttons stay hidden and the status poll never starts.
  const finicityEnabled = isFinicityEnabled(configuration);
  const userCanGenerateFinicityLink: boolean = finicityEnabled && isNotNil(currentEmployee) && userHasPermission(activeUser, permissions.finicityBankVerification, PermissionSet.C);
  const userCanViewFinicityReport: boolean = finicityEnabled && isNotNil(currentEmployee) && userHasPermission(activeUser, permissions.finicityBankVerification, PermissionSet.R);
  const [isGeneratingCustomerPortalLink, setIsGeneratingCustomerPortalLink] = useState<boolean>(false);
  const [bankVerificationStatusInfo, setbankVerificationStatusInfo] =
    useState<{ primaryStatus: string, coApp1Status: string, coApp2Status: string, primaryStatusDate, coApp1StatusDate, coApp2StatusDate }>
      ({ primaryStatus: "", coApp1Status: "", coApp2Status: "", primaryStatusDate: new Date(), coApp1StatusDate: new Date(), coApp2StatusDate: new Date() });

  // Used to calculate the payment to income ratio.
  const incomeRows = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.incomeRows) || [];
  // Used when calculating the payment to income ratio.
  const quotePayTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  // Key Factor validation results
  const keyFactorValidationResults: KeyFactorValidationResult[] = useSelector((state: AppState) => state.applications.keyFactorValidationResults);
  // Income and debt info is used to calculate the payment to income ratio.
  const incomeAndDebtInfo = getIncomeAndDebtBreakdownInternal(
    props.currentApp.applicationBudget, props.currentApp.appCustomers || [],
    undefined,
    props.currentApp.includeAllCurrentIncomes,
    incomeRows?.find(r => r.name === 'Additional Income')?.isAutomaticallyCalculated ?? false);
  // Which quote to use for PTI and DTI calculations. If financeQuotes is empty, use the applicationLeaseQuotes, otherwise use the applicationQuotes.
  const quoteToUseForCalculations: IQuote | IApplicationLeaseQuote | FinanceQuote = props.currentApp.financeQuotes.find(afq => afq.isSelected)
    ?? props.currentApp.applicationLeaseQuote
    ?? props.currentApp.applicationQuotes.find(aq => !aq.deleted && aq.quote.quoteType === IndirectQuoteType.Underwriter)?.quote;
  // Different quote types have different property names for storing the amount to finance.
  const amountToFinanceForQuote = (quoteToUseForCalculations as IQuote | IApplicationLeaseQuote)?.amountToFinance ?? quoteToUseForCalculations?.amountFinanced ?? undefined;
  // Number of payments per year for the selected AMS pay type.
  const selectedAmsPayTypePaymentsPerYear = usesFinanceQuote(props.currentApp.quoteType) && quoteToUseForCalculations
    ? getNumberOfPaymentsPerYearForCarletonPaymentFrequency((quoteToUseForCalculations as FinanceQuote).paymentFrequency) 
    : quotePayTypes.find(qpt => qpt.id?.toLowerCase() === (quoteToUseForCalculations as IApplicationLeaseQuote)?.paymentFrequencyId?.toLowerCase())?.paymentsPerYear ?? 12;
  // The total debt used for the DTI calculations.
  const totalDebtUsed = new Big(incomeAndDebtInfo.totalDebtUsed);
  // The total debt minus the payment that has been submitted.
  const totalDebtMinusSubmittedPayment = totalDebtUsed.sub(quoteToUseForCalculations?.payment ?? 0);
  // The total debt used for the DTI calculations plus the payment that has been submitted.
  const totalDebt = totalDebtMinusSubmittedPayment.add(Big(configuration?.featureFlags?.[FeatureFlagIds.ExcludePaymentFromDebtToIncome] === true ? 0 : quoteToUseForCalculations?.payment ?? 0));
  // Debt to income ratio.
  const debtToIncomeRatio = quoteToUseForCalculations ? `${debtToIncome(Number.parseFloat(totalDebt.toFixed(7)), incomeAndDebtInfo.totalIncomeUsed).toFixed(2)}%` : undefined;
  // Payment to income ratio.
  const paymentToIncomeRatioWithPayment = getPaymentToIncomeRatioWithPayment(props.currentApp, quotePayTypes);
  const paymentToIncomeRatioString = paymentToIncomeRatioWithPayment.paymentToIncomeRatio.toFixed(2);
  // The sum of all monthly payments for auto loans for all applicants.
  const getAutoLoanMonthlyPayment = (): number => {
    const customerCreditValuePrimary = (primary?.customer?.customerCredit.length ?? 0 > 0) ? getCustomerCreditValue(primary!.customer.customerCredit[0], 'monthly payment for auto loans') : undefined;
    const overrideValuePrimary = getCustomFieldValue([...primary?.customer?.customerCustomFields ?? [] as IEntityCustomField[]], 'autoLoansMonthlyPayment');
    const customerCreditValueCoApp1 = (coApp1?.customer?.customerCredit.length ?? 0 > 0) ? getCustomerCreditValue(coApp1!.customer!.customerCredit[0], 'monthly payment for auto loans') : undefined;
    const overrideValueCoApp1 = getCustomFieldValue([...coApp1?.customer?.customerCustomFields ?? [] as IEntityCustomField[]], 'autoLoansMonthlyPayment');
    const customerCreditValueCoApp2 = (coApp2?.customer?.customerCredit.length ?? 0 > 0) ? getCustomerCreditValue(coApp2!.customer.customerCredit[0], 'monthly payment for auto loans') : undefined;
    const overrideValueCoApp2 = getCustomFieldValue([...coApp2?.customer?.customerCustomFields ?? [] as IEntityCustomField[]], 'autoLoansMonthlyPayment');

    return +(overrideValuePrimary || customerCreditValuePrimary || 0) + +(overrideValueCoApp1 || customerCreditValueCoApp1 || 0) + +(overrideValueCoApp2 || customerCreditValueCoApp2 || 0);
  }
  // The sum of all monthly total payments for the student loans section.
  const getStudentLoanMonthlyPayment = (): number => {
    const customerCreditValuePrimary = (primary?.customer?.customerCredit.length ?? 0 > 0) ? getCustomerCreditValue(primary!.customer.customerCredit[0], 'total monthly payment') : undefined;
    const overrideValuePrimary = getCustomFieldValue([...primary?.customer?.customerCustomFields ?? [] as IEntityCustomField[]], 'studentLoansMonthlyPayment');
    const customerCreditValueCoApp1 = (coApp1?.customer?.customerCredit.length ?? 0 > 0) ? getCustomerCreditValue(coApp1!.customer.customerCredit[0], 'total monthly payment') : undefined;
    const overrideValueCoApp1 = getCustomFieldValue([...coApp1?.customer?.customerCustomFields ?? [] as IEntityCustomField[]], 'studentLoansMonthlyPayment');
    const customerCreditValueCoApp2 = (coApp2?.customer?.customerCredit.length ?? 0 > 0) ? getCustomerCreditValue(coApp2!.customer.customerCredit[0], 'total monthly payment') : undefined;
    const overrideValueCoApp2 = getCustomFieldValue([...coApp2?.customer?.customerCustomFields ?? [] as IEntityCustomField[]], 'studentLoansMonthlyPayment');
    return + (overrideValuePrimary || customerCreditValuePrimary || 0) + (overrideValueCoApp1 || customerCreditValueCoApp1 || 0) + (overrideValueCoApp2 || customerCreditValueCoApp2 || 0);
  }
  // Get the car payment in monthly payment format.
  const monthlyCarPayment = getConvertedMonthlyPaymentAmount(selectedAmsPayTypePaymentsPerYear!, quoteToUseForCalculations?.payment ?? 0);
  const monthlyPaymentWithOpenAutoLoans: number = monthlyCarPayment + getAutoLoanMonthlyPayment();
  // Payment to income ratio with open auto loans included.
  const paymentToIncomeWithOpenAutoLoans = quoteToUseForCalculations ? paymentToIncome(monthlyPaymentWithOpenAutoLoans, 12, incomeAndDebtInfo.totalIncomeUsed).toFixed(2) : undefined;
  // Term
  const term = quoteToUseForCalculations?.termInMonths ?? (quoteToUseForCalculations as IApplicationLeaseQuote)?.term;
  // Calculates the monthly estimated loan payments. we want add the Monthly Payment For Auto Loans and Total Monthly Payment values. We need to check if the values has been overriden or not, depending on if the override value is present or not.
  const getEstimatedMonthlyLoanPayments = (): number => {
    const result = getAutoLoanMonthlyPayment() + getStudentLoanMonthlyPayment();
    return result;
  }

  // returns the total number of open autos for all applicants
  const getTotalOpenAutos = (): number => {
    const openAutosForPrimary = primary
      ? primary!.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofopenautos')?.value
        ?? primary!.customer?.customerCredit[0]?.numberOfOpenAutos
      : undefined;
    const openAutosForCoApp1 = coApp1
      ? coApp1!.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofopenautos')?.value
        ?? coApp1!.customer?.customerCredit[0]?.numberOfOpenAutos
      : undefined;
    const openAutosForCoApp2 = coApp2
      ? coApp2!.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofopenautos')?.value
        ?? coApp2!.customer?.customerCredit[0]?.numberOfOpenAutos
      : undefined;
    return (openAutosForPrimary ? +openAutosForPrimary : 0) + (openAutosForCoApp1 ? +openAutosForCoApp1 : 0) + (openAutosForCoApp2 ? +openAutosForCoApp2 : 0);
  }

  // returns the total number of paid-off autos for all applicants.
  const getTotalPaidOffAutos = (): number => {
    const paidOffAutosForPrimary = primary
      ? primary!.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofautospaid')?.value
        ?? primary!.customer?.customerCredit[0]?.numberOfAutosPaid
      : undefined;
    const paidOffAutosForCoApp1 = coApp1
      ? coApp1!.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofautospaid')?.value
        ?? coApp1!.customer?.customerCredit[0]?.numberOfAutosPaid
      : undefined;
    const paidOffAutosForCoApp2 = coApp2
      ? coApp2!.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofautospaid')?.value
        ?? coApp2!.customer?.customerCredit[0]?.numberOfAutosPaid
      : undefined;
    return (paidOffAutosForPrimary ? +paidOffAutosForPrimary : 0) + (paidOffAutosForCoApp1 ? +paidOffAutosForCoApp1 : 0) + (paidOffAutosForCoApp2 ? +paidOffAutosForCoApp2 : 0);
  }

  // Once when page loads
  useEffect(() => {
    const update = async () => {
      if (userCanViewFinicityReport) {
        const response = await getCurrentFincityStatus(props.currentApp.id, dispatch);
        const result = response.status;
        setbankVerificationStatusInfo({
          primaryStatus: result.primaryStatus, coApp1Status: result.coApp1Status, coApp2Status: result.coApp2Status,
          primaryStatusDate: result.primaryStatusDate, coApp1StatusDate: result.coApp1StatusDate, coApp2StatusDate: result.coApp2StatusDate
        });
      }
    };
    if (userCanViewFinicityReport) {
      update();
    }
  }, []);

  // Then set a timer to update every 5 mins if they stay on the page
  useEffect(() => {
    let timer = setInterval(async () => {
      if (userCanViewFinicityReport) {
        const response = await getCurrentFincityStatus(props.currentApp.id, dispatch);
        const result = response.status;
        setbankVerificationStatusInfo({
          primaryStatus: result.primaryStatus, coApp1Status: result.coApp1Status, coApp2Status: result.coApp2Status,
          primaryStatusDate: result.primaryStatusDate, coApp1StatusDate: result.coApp1StatusDate, coApp2StatusDate: result.coApp2StatusDate
        });
      }
    }, 5 * MS_PER_MINUTE); // 300,000 = 5 minutes * 60 seconds * 1000 milliseconds;
    return () => clearInterval(timer);
  }, [bankVerificationStatusInfo, props.currentApp]);

  const handleGenerateFinicityLinkButtonClick = (customer: ICustomer, primaryApplicant: boolean): void => {
    const finicityValidationErrors: string[] = validateFinicityInfo(customer);
    if (finicityValidationErrors.length > 0) {
      openDialog({
        children: (
          <ErrorDialog
            title="Finicity Validation Errors"
            errors={finicityValidationErrors}
            closeDialog={closeDialog}
          />
        ),
      });
    } else {
      if (primaryApplicant) {
        openSimpleMessagingForFinicityLinkDialog(LinkType.bankVerificationLinkApplicant);
      }
      else {
        openSimpleMessagingForFinicityLinkDialog(LinkType.bankVerificationLinkCoApplicant);
      }
    }
  };


  const openSimpleMessagingForFinicityLinkDialog = async (linkType: LinkType) => {
    try {
      if (!props.currentApp) {
        return;
      }

      // set the isGeneratingCustomerPortalLink flag to true so we can show a loading indicator
      setIsGeneratingCustomerPortalLink(true);

      const response: { portalLink: string } | { error: string } = await generateCustomerPortalFinicityLinkUtil(props.currentApp.contactOpportunityId!, linkType, dispatch)

      // show an error if we were unable to generate a customer portal link
      if (hasValue((response as { error: string }).error)) {
        dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: (response as { error: string }).error, isError: true } });
        return;
      }

      if (!hasValue((response as { portalLink: string }).portalLink)) {
        dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Unable to generate a Finicity link. Please try again.', isError: true } });
        return;
      }

      const portalLink = (response as { portalLink: string }).portalLink;

      // Gets both the text and email templates filled at the same time
      const [filledEmailTemplateSubjectAndBody, filledTextTemplateSubjectAndBody] = await Promise.all([
        getFilledTemplate(props.currentApp.contactOpportunityId!, MessageTemplates.continueApplicationEmail, dispatch),
        getFilledTemplate(props.currentApp.contactOpportunityId!, MessageTemplates.continueApplicationText, dispatch)
      ]);

      (filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body =
        ((filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body ?? '') + '<p>' + portalLink + '</p>';
      (filledTextTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body =
        ((filledTextTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body ?? '') + '\n\n' + portalLink;

      setIsGeneratingCustomerPortalLink(false);

      const recipientOptions = getCustomerPortalLinkMenuOptionsByRole(props.currentApp, linkType);

      openDialog({
        children: (
          <SimpleMessagingDialog
            dialogTitle={'Send Bank Authorization Message'}
            onClose={closeDialog}
            selectableRecipients={recipientOptions}
            defaultEmailSubject={(filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).subject}
            defaultEmailBody={(filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body}
            defaultTextMessage={(filledTextTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body}
            onSendEmailMessage={sendFinicityEmailMessagesToContact}
            onSendTextMessage={sendFinicityTextMessagesToContact}
          />
        ),
      });
    } catch (error) {
      dispatch({
        type: "SET_SERVER_MESSAGE",
        payload: { message: error as string, isError: true }
      });
      setIsGeneratingCustomerPortalLink(false);
    }
  };

  const sendFinicityEmailMessagesToContact = (emailMessage: SimpleEmailMessage) => {
    sendFinicityEmailMessagesToContactUtil(props.currentApp!, emailMessage, dispatch, closeDialog);
  }

  const sendFinicityTextMessagesToContact = (textMessage: SimpleTextMessage) => {
    sendFinicityTextMessagesToContactUtil(props.currentApp!, textMessage, currentEmployee, dispatch, closeDialog);
  }
  
  const totalRepossessions = getTotalRepossessions(props.currentApp);

  return (
    <Accordion expanded={isAccordionExpanded} onChange={() => setIsAccordionExpanded(!isAccordionExpanded)} className={styles.applicationSummaryCardContainer}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} >
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="body2" style={{ display: 'flex', alignItems: 'center' }}><InfoIcon fontSize="small" style={{ marginRight: '1em' }} />
            Key Factors
          </Typography>
          <div style={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
            {hasValue(props.contactDetailUrl) && (
              <>
                <Tooltip title="View customer in CRM">
                  <Button
                    component="span"
                    variant="text"
                    color="inherit"
                    style={{ color: '#757575', marginRight: '5px', pointerEvents: 'all', opacity: 1, textDecoration: 'underline', textUnderlineOffset: '2px', textTransform: 'none' }}
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); window.open(props.contactDetailUrl, '_blank'); }}
                  >
                    CRM
                    <OpenInNewIcon fontSize="small" style={{ marginLeft: '3px' }} />
                  </Button>
                </Tooltip>
              </>
            )}
            <Tooltip title="Edit applicant info">
              <IconButton component="span" size="small" onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.navigate("Applicant"); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ margin: 0, padding: 0 }}>
        <TabStrip
          selected={tabSelected}
          onSelect={(e: TabStripSelectEventArguments) => setTabSelected(e.selected)}
          style={{ width: '100%' }}
        >
          {primary &&
            <TabStripTab title={'Applicant'}>
              <ApplicationSummaryTab
                customer={primary.customer}
                businessRuleOverrides={props.currentApp.businessRuleOverrides}
                applicationDate={props.currentApp.applicationDate}
                applicationNumber={props.currentApp.applicationNo}
                customFields={primary.customer.customerCustomFields}
                paymentToIncome={paymentToIncomeRatioString}
                paymentToIncomeWithOpenAutoLoans={paymentToIncomeWithOpenAutoLoans}
                incomeForApplicant={incomeAndDebtInfo.totalIncomePrimary}
                monthlyPayment={monthlyCarPayment.toFixed(2)}
                downPayment={(quoteToUseForCalculations as IApplicationLeaseQuote)?.cashDown ?? quoteToUseForCalculations?.downPayment ?? 0}
                debtToIncome={debtToIncomeRatio}
                otherLoanPayments={getEstimatedMonthlyLoanPayments().toFixed(2)}
                numberOfRepossessions={totalRepossessions}
                numberOfOpenAutos={getTotalOpenAutos()}
                numberOfPaidOffAutos={getTotalPaidOffAutos()}
                term={term}
                amountToFinance={amountToFinanceForQuote}
                keyFactorValidationResults={keyFactorValidationResults}
                onViewConsentHistory={((primary?.customer?.consentHistoryItems?.length || 0) > 0) ? () => {
                  setConsentHistoryCustomer({
                    name: primary!.customer.firstName + ' ' + primary!.customer.lastName,
                    items: primary!.customer.consentHistoryItems
                  });
                } : undefined}
              />
              {userCanViewFinicityReport &&
                <Button
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: "5px", marginLeft: "5px" }}
                  onClick={() => handleGenerateFinicityLinkButtonClick(primary.customer, true)}
                  disabled={!userCanGenerateFinicityLink}
                >
                  <AccountBalanceIcon style={{ marginRight: "5px" }} />
                  Income Verification (Applicant)<span style={{ textTransform: "none", marginLeft: "5px" }}> {bankVerificationStatusInfo.primaryStatus.length > 0
                    ? `(${bankVerificationStatusInfo.primaryStatus} ${formatDateOrTimeString(bankVerificationStatusInfo.primaryStatusDate.toLocaleString())})`
                    : ''}</span>
                </Button>
              }
            </TabStripTab>
          }
          {coApp1 &&
            <TabStripTab title={'Co-applicant 1'}>
              <ApplicationSummaryTab
                customer={coApp1.customer}
                businessRuleOverrides={props.currentApp.businessRuleOverrides}
                applicationDate={props.currentApp.applicationDate}
                applicationNumber={props.currentApp.applicationNo}
                customFields={coApp1.customer.customerCustomFields}
                paymentToIncome={paymentToIncomeRatioString}
                paymentToIncomeWithOpenAutoLoans={paymentToIncomeWithOpenAutoLoans}
                monthlyPayment={monthlyCarPayment.toFixed(2)}
                otherLoanPayments={getEstimatedMonthlyLoanPayments().toFixed(2)}
                incomeForApplicant={incomeAndDebtInfo.totalIncomeCobuyer1}
                debtToIncome={debtToIncomeRatio}
                numberOfRepossessions={totalRepossessions}
                numberOfOpenAutos={getTotalOpenAutos()}
                numberOfPaidOffAutos={getTotalPaidOffAutos()}
                term={term}
                amountToFinance={amountToFinanceForQuote}
                keyFactorValidationResults={keyFactorValidationResults}
                onViewConsentHistory={((coApp1?.customer?.consentHistoryItems?.length || 0) > 0) ? () => {
                  setConsentHistoryCustomer({
                    name: coApp1!.customer.firstName + ' ' + coApp1!.customer.lastName,
                    items: coApp1!.customer.consentHistoryItems
                  });
                } : undefined}
              />
              {userCanViewFinicityReport &&
                <Button
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: "5px", marginLeft: "5px" }}
                  onClick={() => handleGenerateFinicityLinkButtonClick(coApp1.customer, false)}
                  disabled={!userCanGenerateFinicityLink}
                >
                  <AccountBalanceIcon style={{ marginRight: "5px" }} />
                  Income Verification (Co-Applicant 1)<span style={{ textTransform: "none", marginLeft: "5px" }}> {bankVerificationStatusInfo.coApp1Status.length > 0
                    ? `(${bankVerificationStatusInfo.coApp1Status} ${formatDateOrTimeString(bankVerificationStatusInfo.coApp1StatusDate.toLocaleString())})`
                    : ''}</span>
                </Button>
              }
            </TabStripTab>
          }
          {coApp2 &&
            <TabStripTab title={'Co-applicant 2'}>
              <ApplicationSummaryTab
                customer={coApp2.customer}
                businessRuleOverrides={props.currentApp.businessRuleOverrides}
                applicationDate={props.currentApp.applicationDate}
                applicationNumber={props.currentApp.applicationNo}
                customFields={coApp2.customer.customerCustomFields}
                paymentToIncome={paymentToIncomeRatioString}
                paymentToIncomeWithOpenAutoLoans={paymentToIncomeWithOpenAutoLoans}
                otherLoanPayments={getEstimatedMonthlyLoanPayments().toFixed(2)}
                monthlyPayment={monthlyCarPayment.toFixed(2)}
                incomeForApplicant={incomeAndDebtInfo.totalIncomeCobuyer2}
                debtToIncome={debtToIncomeRatio}
                numberOfRepossessions={totalRepossessions}
                numberOfOpenAutos={getTotalOpenAutos()}
                numberOfPaidOffAutos={getTotalPaidOffAutos()}
                term={term}
                amountToFinance={amountToFinanceForQuote}
                keyFactorValidationResults={keyFactorValidationResults}
                onViewConsentHistory={((coApp2?.customer?.consentHistoryItems?.length || 0) > 0) ? () => {
                  setConsentHistoryCustomer({
                    name: coApp2!.customer.firstName + ' ' + coApp2!.customer.lastName,
                    items: coApp2!.customer.consentHistoryItems
                  });
                } : undefined}
              />
              {userCanViewFinicityReport && (
                <Button
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: "5px", marginLeft: "5px" }}
                  onClick={() => handleGenerateFinicityLinkButtonClick(coApp2.customer, false)}
                  disabled={!userCanGenerateFinicityLink}
                >
                  <AccountBalanceIcon style={{ marginRight: "5px" }} />
                  Income Verification (Co-Applicant 2)<span style={{ textTransform: "none", marginLeft: "5px" }}> {bankVerificationStatusInfo.coApp2Status.length > 0
                    ? `(${bankVerificationStatusInfo.coApp2Status} ${formatDateOrTimeString(bankVerificationStatusInfo.coApp2StatusDate.toLocaleString())})`
                    : ''}</span>
                </Button>
              )}
            </TabStripTab>
          }
          <TabStripTab title="Budget">
            <BudgetTable
              {...props}
              primary={primary}
              coApp1={coApp1}
              coApp2={coApp2}
              configuration={configuration}
            />
          </TabStripTab>
        </TabStrip>
      </AccordionDetails>
      {consentHistoryCustomer && (
        <ConsentHistoryDialog
          customerName={consentHistoryCustomer.name}
          historyItems={consentHistoryCustomer.items}
          onClose={() => setConsentHistoryCustomer(null)}
        />
      )}
    </Accordion>
  );
}

interface ApplicationSummaryTabProps {
  customer: ICustomer;
  businessRuleOverrides: BusinessRuleOverride[]; // So we know which fields have been overridden
  applicationDate: Date; // The date the application was created
  applicationNumber: number; // The application number
  customFields: IEntityCustomField[]; // Custom fields for the application or customer, depending on the feature flag
  incomeForApplicant?: number; // The total income for the applicant
  paymentToIncome?: string; // The payment to income ratio
  amountToFinance?: number; // The amount to finance on the deal
  debtToIncome?: string; // The debt to income ratio
  downPayment?: number; // The down payment amount
  paymentToIncomeWithOpenAutoLoans?: string; // The payment to income ratio with open auto loans included
  numberOfRepossessions: number; // The total number of repossessions for all applicants
  numberOfOpenAutos: number; // The total number of open autos for all applicants
  numberOfPaidOffAutos: number; // The total number of paid-off autos for all applicants
  otherLoanPayments?: string; // The sum of all monthly payments for auto loans for all applicants
  monthlyPayment?: string; // The monthly payment for the selected quote
  term?: number; // The term for the selected quote
  // keyFactorThresholds: KeyFactorThreshold[]; // Key Factor thresholds so we can show green/yellow/red indicators if the value is good, warning, or bad.
  keyFactorValidationResults: KeyFactorValidationResult[]; // Key Factor thresholds so we can show green/yellow/red indicators if the value is good, warning, or bad.
  onViewConsentHistory?: () => void; // Function to open the consent history dialog when the "View Consent" button is clicked
}

function ApplicationSummaryTab(props: ApplicationSummaryTabProps) {
  const downPayment = props.downPayment ?? 0;
  return (
    <div className={styles.applicationSummaryCardContainer}>
      <SummaryValueRow
        {...props}
        includeLefMarginForIcon={true}
        title={'Applicant'}
        value={hasValue(getFullName(props.customer)) ? capitalizeFirstLetterOfEachWord(getFullName(props.customer)!) : ''}
      />
      <SummaryValueRow
        {...props}
        includeLefMarginForIcon={true}
        title={'Customer Number'}
        value={props.customer.amsCustomerNumber ?? ''}
      />
      <SummaryValueRow
        {...props}
        title={'Credit Score'}
        includeLefMarginForIcon={true}
        value={props.customer.customerCredit?.[0]?.bureauScore}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'bureauscore')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'bureauscore')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Age'}
        includeLefMarginForIcon={true}
        value={`${getAge(props.customer)} yrs`}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'age')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'age')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Income'}
        includeLefMarginForIcon={true}
        value={props.incomeForApplicant ? toDollars(props.incomeForApplicant) : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'income')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'income')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Finance Amount'}
        includeLefMarginForIcon={true}
        value={props.amountToFinance ? toDollars(props.amountToFinance) : ''}
        keyFactorValidationResult={getKeyFactorValidationResult(props.keyFactorValidationResults, ['minamounttofinance', 'maxamounttofinance'])}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'amounttofinance')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Down Pmt'}
        includeLefMarginForIcon={true}
        value={downPayment?.toLocaleString() ? toDollars(downPayment) : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'mindownpayment')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'cashDown')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Down Pmt %'}
        includeLefMarginForIcon={true}
        value={props.amountToFinance ? `${((downPayment ?? 0) / props.amountToFinance * 100).toFixed(2)}%` : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'mindownpaymentpercent')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'cashDown_1')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Job Time'}
        includeLefMarginForIcon={true}
        value={getPrimaryJobTime(props.customer.customerEmployment, props.applicationDate)}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'jobtime')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'jobtime')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Residence Time'}
        includeLefMarginForIcon={true}
        value={getCurrentResidenceTime(props.customer.customerAddress, props.applicationDate)}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'residencetime')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'residencetime')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Other Loan Payments'}
        includeLefMarginForIcon={true}
        value={props.otherLoanPayments ? `$${props.otherLoanPayments}` : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'otherloanpayments')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'rentmortgagepayment')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Monthly Pmt (This Loan)'}
        includeLefMarginForIcon={true}
        value={props.monthlyPayment ? `$${props.monthlyPayment}` : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'rentmortgagepayment')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'rentmortgagepayment')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'PTI'}
        includeLefMarginForIcon={true}
        value={props.paymentToIncome ? `${props.paymentToIncome}%` : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'paymenttoincome')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'paymenttoincome')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'PTI with Open Auto Loans'}
        includeLefMarginForIcon={true}
        value={props.paymentToIncomeWithOpenAutoLoans ? `${props.paymentToIncomeWithOpenAutoLoans}%` : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'paymenttoincomewithopenautoloans')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'paymenttoincomewithopenautoloans')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'DTI'}
        includeLefMarginForIcon={true}
        value={props.debtToIncome ?? ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'debttoincome')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'debttoincome')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Term'}
        includeLefMarginForIcon={true}
        value={props.term ? `${props.term} mos` : ''}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'term')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'term')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Repossession'}
        includeLefMarginForIcon={true}
        value={props.numberOfRepossessions}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'numberofrepossessions')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'numberofrepossessions')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Open Autos'}
        includeLefMarginForIcon={true}
        value={props.numberOfOpenAutos}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'numberofopenautos')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'numberofopenautos')?.businessRuleOverrideStatusId}
      />
      <SummaryValueRow
        {...props}
        title={'Paid-off Loans'}
        includeLefMarginForIcon={true}
        value={props.numberOfPaidOffAutos}
        keyFactorValidationResult={props.keyFactorValidationResults.find(kfvr => kfvr.threshold.fieldName.toLowerCase() === 'numberofautospaid')}
        businessRuleOverrideStatusId={props.businessRuleOverrides.find(bro => bro.applicationField?.toLowerCase() === 'numberofautospaid')?.businessRuleOverrideStatusId}
      />
      {
        isNotNil(props.onViewConsentHistory) && (
          <Button
            variant="text"
            color="inherit"
            style={{ fontSize: '14px', fontWeight: 600, paddingLeft: '0', textDecoration: 'underline', textUnderlineOffset: '2px', textTransform: 'none', width: 'fit-content' }}
            onClick={() => props.onViewConsentHistory && props.onViewConsentHistory()}
          >
            View Consent
          </Button>
        )
      }
    </div>
  );
}

export interface SummaryValueRowProps {
  title: string;
  value: string | number | undefined;
  keyFactorValidationResult?: KeyFactorValidationResult; // The KeyFactorValidationResult for this value
  includeLefMarginForIcon?: boolean; // Whether or not to include left margin for the icon
  businessRuleOverrideStatusId?: string | null;// ID of the status of the businessRuleOverride record associated with this field. If on "Hold" or "Waived" status, we want to show that in the UI insteas of a green checkmark or red x.
}

export function SummaryValueRow(props: SummaryValueRowProps) {
  // returns true if the item's businessRuleOverrideStatusId is "Waived"
  const isWaived = (): boolean => {
    return props.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase();
  }
  // returns the tooltip for the row based on the KeyFactorThresholdSection and whether the businessRuleOverrideStatusId is "Hold" or "Waived"
  const getTooltipForRow = (keyFactorThresholdSection?: KeyFactorThresholdSection) => {
    // If the businessRuleOverrideStatusId is "Waived" we want to show that in the tooltip instead of the KeyFactorThresholdSection
    if (props.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase()) {
      return `${props.title} is waived. You will not need to bypass this requirement when submitting the application.`;
    }

    // If no KeyFactorThresholdSection is provided or found, return undefined
    if (!keyFactorThresholdSection) return undefined;
    // Based on the KeyFactorThresholdSection, return the appropriate tooltip
    switch (keyFactorThresholdSection) {
      case KeyFactorThresholdSection.Bad:
        {
          return props.keyFactorValidationResult?.threshold?.direction === '1'
            ? `${props.value} is below the threshold of ${props.keyFactorValidationResult?.threshold?.minWarnThreshold}`
            : `${props.value} is above the threshold of ${props.keyFactorValidationResult?.threshold?.minWarnThreshold}`;
        }
      case KeyFactorThresholdSection.Warn:
        {
          return props.keyFactorValidationResult?.threshold?.direction === '1'
            ? `${props.value} is between the thresholds of ${props.keyFactorValidationResult?.threshold?.minWarnThreshold} and ${props.keyFactorValidationResult?.threshold?.minGoodThreshold}`
            : `${props.value} is between the thresholds of ${props.keyFactorValidationResult?.threshold?.minGoodThreshold} and ${props.keyFactorValidationResult?.threshold?.minWarnThreshold}`;
        }
      case KeyFactorThresholdSection.Good:
        {
          return props.keyFactorValidationResult?.threshold?.direction === '1'
            ? `${props.value} is at or above the threshold of ${props.keyFactorValidationResult?.threshold?.minGoodThreshold}`
            : `${props.value} is at or below the threshold of   ${props.keyFactorValidationResult?.threshold?.minGoodThreshold}`;
        }
      default:
        return '';
    }
  }
  // returns the appropriate SASS class name
  const getClassNameForRow = (): string => {
    if (props.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase()) {
      return styles.waived;
    }
    if (props.keyFactorValidationResult?.section === KeyFactorThresholdSection.Bad) {
      return styles.bad;
    }
    if (props.keyFactorValidationResult?.section === KeyFactorThresholdSection.Warn) {
      return styles.warn;
    }
    if (props.keyFactorValidationResult?.section === KeyFactorThresholdSection.Good) {
      return styles.good;
    }
    return '';
  }
  // returns the appropriate icon based on the KeyFactorThresholdSection
  const getIconForRow = (): JSX.Element | undefined => {
    if (props.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase()) {
      return <PriorityHighIcon fontSize="small" />;
    }
    if (props.keyFactorValidationResult?.section === KeyFactorThresholdSection.Bad) {
      return <PriorityHighIcon fontSize="small" />;
    }
    if (props.keyFactorValidationResult?.section === KeyFactorThresholdSection.Warn) {
      return <PriorityHighIcon fontSize="small" />;
    }
    if (props.keyFactorValidationResult?.section === KeyFactorThresholdSection.Good) {
      return <CheckIcon fontSize="small" />;
    }
    return undefined;
  }
  return (
    <div className={styles.applicationSummaryCardContainer}>
      <Tooltip title={getTooltipForRow(props.keyFactorValidationResult?.section)}>
        <div className={styles.rowItem}>
          {props.keyFactorValidationResult?.section && isWaived() !== true && (
            <div className={`${getClassNameForRow()} ${styles.label}`}>
              {getIconForRow()}
              {props.title + ':'}
            </div>
          )}
          {isWaived() && (
            <div className={`${styles.waived} ${styles.label}`}>
              {'W'}&nbsp;&nbsp;{props.title + ':'}
            </div>
          )}
          {!props.keyFactorValidationResult?.section && isWaived() !== true && <div style={props.includeLefMarginForIcon ? { fontWeight: 'bold', marginLeft: '23px' } : {}}>{props.title + ':'}</div>}
          <div className={getClassNameForRow()}>
            {props.value ?? ''}
          </div>
        </div>
      </Tooltip>
    </div>
  );
}

interface BudgetTableProps {
  currentApp: IApplication;
  primary: IAppCustomer | undefined;
  coApp1: IAppCustomer | undefined;
  coApp2: IAppCustomer | undefined
  configuration: IConfiguration | undefined;
}

function BudgetTable(props: BudgetTableProps) {
  const incomeRows = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.incomeRows) || [];
  const incomeAndDebtInfo = getIncomeAndDebtBreakdownInternal(
    props.currentApp.applicationBudget, props.currentApp.appCustomers || [],
    undefined,
    props.currentApp.includeAllCurrentIncomes,
    incomeRows?.find(r => r.name === 'Additional Income')?.isAutomaticallyCalculated ?? false);
  const includeCo2 = props.configuration?.maxNumberOfApplicants ? props.configuration?.maxNumberOfApplicants > 2 : false;

  return (
    <div className={styles.budgetTable}>
      <div className={styles.budgetRow}>
        <div className={styles.budgetRowHeader}></div>
        {props.primary && <div className={styles.budgetColumnHeader}>Applicant</div>}
        {props.coApp1 && <div className={styles.budgetColumnHeader}>Co-App</div>}
        {props.coApp2 && <div className={styles.budgetColumnHeader}>Co-App 2</div>}
      </div>
      <div className={styles.budgetRow}>
        <div className={styles.budgetRowHeader}>Income:</div>
        {props.primary && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalIncomePrimary?.toFixed(2)}</div>}
        {props.coApp1 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalIncomeCobuyer1?.toFixed(2)}</div>}
        {props.coApp2 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalIncomeCobuyer2?.toFixed(2)}</div>}
      </div>
      <div className={styles.budgetRow}>
        <div className={styles.budgetRowHeader}>Debt:</div>
        {props.primary && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalDebtPrimary?.toFixed(2)}</div>}
        {props.coApp1 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalDebtCobuyer1?.toFixed(2)}</div>}
        {props.coApp2 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalDebtCobuyer2?.toFixed(2)}</div>}
      </div>
      <div className={styles.budgetRow}>
        <div className={styles.budgetRowHeader}>Net:</div>
        {props.primary && <div className={styles.budgetCellValue}>{incomeAndDebtInfo && (incomeAndDebtInfo.totalIncomePrimary - incomeAndDebtInfo.totalDebtPrimary)?.toFixed(2)}</div>}
        {props.coApp1 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo && (incomeAndDebtInfo.totalIncomeCobuyer1 - incomeAndDebtInfo.totalDebtCobuyer1)?.toFixed(2)}</div>}
        {props.coApp2 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo && (incomeAndDebtInfo.totalIncomeCobuyer2 - incomeAndDebtInfo.totalDebtCobuyer2)?.toFixed(2)}</div>}
      </div>
      {!!props.configuration?.maxPaymentAsPercentageOfNetIncome &&
        <div className={styles.budgetRow}>
          <div className={styles.budgetRowHeader}>Max Payment:</div>
          {props.primary && <div className={styles.budgetCellValue}>{incomeAndDebtInfo?.totalIncomePrimary?.toFixed(2)}</div>}
          {props.coApp1 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo && ((incomeAndDebtInfo.totalIncomeCobuyer1 - incomeAndDebtInfo.totalDebtCobuyer1) * props.configuration.maxPaymentAsPercentageOfNetIncome)?.toFixed(2)}</div>}
          {includeCo2 && props.coApp2 && <div className={styles.budgetCellValue}>{incomeAndDebtInfo && ((incomeAndDebtInfo.totalIncomeCobuyer2 - incomeAndDebtInfo.totalDebtCobuyer2) * props.configuration.maxPaymentAsPercentageOfNetIncome)?.toFixed(2)}</div>}
        </div>
      }
    </div>
  );
}
